import {
  AfterViewInit,
  Directive,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  Renderer2
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';

declare var $: any;

@Directive({
  selector: '[multiSelect2]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Select2MultiDirective),
      multi: true
    }
  ]
})
export class Select2MultiDirective implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @Input() fetchFn!: (term: string) => Observable<any[]>;
  @Input() formCtrl: FormControl | null = null;
  @Input() meta: {
    multiple: boolean;
    keys: {
      id: string;
      name: string;
    };
  } = {
    multiple: false,
    keys: {
      id: 'id',
      name: 'name',
    },
  };

  private onChange = (_: any) => {};
  private onTouched = () => {};
  private activeRequest: Subscription | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    const el = this.el.nativeElement;
    const $el = $(el);
    const self = this;

    this.meta.multiple = this.meta.multiple || false;
    this.meta.keys = this.meta.keys || {
      id: 'id',
      name: 'name',
    };
    $el.select2({
      placeholder: 'Search...',
      minimumInputLength: 2,
      multiple: this.meta.multiple,
      width: '100%',
      ajax: {
        delay: 1000,
        transport: (params: any, success: any, failure: any) => {
          const term = params.data.term || '';
          const abortController = {
            abort: () => this.activeRequest?.unsubscribe(),
          };

          if (this.fetchFn) {
            this.activeRequest = this.fetchFn(term).subscribe({
              next: (results: any[]) => {
                success({
                  results: results.map((item) => ({
                    id: item[self.meta.keys.id],
                    text: item[self.meta.keys.name],
                  })),
                });
              },
              error: () => failure(),
            });
          }

          return abortController;
        },
      },
    });

    // Prefill values if any
    const prefill = this.formCtrl?.value || [];
    if (prefill && prefill instanceof Array && prefill.length > 0) {
      prefill.forEach((item: any) => {
        const option = new Option(item.name || item.text, item.id, true, true);
        $el.append(option);
      });
      $el.trigger('change');
    }else if(prefill && Object.keys(prefill)?.length){
      const option = new Option(prefill.name, prefill.id, true, true);
      $el.append(option);
      $el.trigger('change');
    }

    $el.on('select2:select select2:unselect', () => {
      const data = $el.select2('data') || [];
      const mapped = data.map((d: any) => ({
        id: d.id,
        name: d.text,
      }));

      const value = this.meta.multiple ? mapped : mapped[0];

      if (this.formCtrl) {
        this.formCtrl.setValue(value);
        this.formCtrl.markAsDirty();
        this.formCtrl.markAsTouched();
        this.updateValidationClass();
      }

      this.onChange(value);
      this.onTouched();

      // Trigger native change detection
      const event = new Event('change', { bubbles: true });
      el.dispatchEvent(event);
    });

    $el.on('select2:close', () => {
      this.onTouched();
      this.updateValidationClass();
    });
  }

  writeValue(value: any): void {
    const $el = $(this.el.nativeElement);
    $el.empty();

    if (value && value.length) {
      value.forEach((item: any) => {
        const option = new Option(item.name || item.text, item.id, true, true);
        $el.append(option);
      });
    }

    setTimeout(() => $el.trigger('change'), 0);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    $(this.el.nativeElement).prop('disabled', isDisabled);
  }

  updateValidationClass(): void {
    if (!this.formCtrl) return;

    const el = this.el.nativeElement;

    // Clear old Angular classes
    const allNgClasses = [
      'ng-touched',
      'ng-untouched',
      'ng-dirty',
      'ng-pristine',
      'ng-valid',
      'ng-invalid',
      'is-invalid'
    ];
    allNgClasses.forEach(cls => this.renderer.removeClass(el, cls));

    // Add classes based on control state
    if (this.formCtrl.touched) {
      this.renderer.addClass(el, 'ng-touched');
    } else {
      this.renderer.addClass(el, 'ng-untouched');
    }

    if (this.formCtrl.dirty) {
      this.renderer.addClass(el, 'ng-dirty');
    } else {
      this.renderer.addClass(el, 'ng-pristine');
    }

    if (this.formCtrl.valid) {
      this.renderer.addClass(el, 'ng-valid');
    } else {
      this.renderer.addClass(el, 'ng-invalid');
    }

    // Add Bootstrap invalid class if needed
    const shouldMarkInvalid =
      this.formCtrl.invalid &&
      (this.formCtrl.touched || this.formCtrl.dirty);

    if (shouldMarkInvalid) {
      this.renderer.addClass(el, 'is-invalid');
    }
  }


  ngOnDestroy(): void {
    $(this.el.nativeElement).select2('destroy');
    this.activeRequest?.unsubscribe();
  }
}
